import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import '../styles/AccountBilling.css';

// Load Stripe.js
const loadStripe = () => {
  return new Promise((resolve) => {
    if (window.Stripe) {
      resolve(window.Stripe);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.onload = () => resolve(window.Stripe);
    document.body.appendChild(script);
  });
};

function AccountBilling() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchData();
    fetchStripeConfig();
  }, []);

  // Check for success/cancelled URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setToast({ message: 'Payment successful! Your subscription is now active.', type: 'success' });
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('cancelled') === 'true') {
      setToast({ message: 'Payment cancelled. You can try again anytime.', type: 'info' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, historyRes] = await Promise.all([
        axios.get('/api/billing/subscription'),
        axios.get('/api/billing/plans'),
        axios.get('/api/billing/history')
      ]);

      setSubscription(subRes.data);
      setAvailablePlans(plansRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setToast({ message: 'Failed to load billing information', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeConfig = async () => {
    try {
      const res = await axios.get('/api/billing/stripe-config');
      setStripeConfig(res.data);
      // Default to monthly if annual isn't available
      if (!res.data.availableBillingCycles?.annual) {
        setBillingCycle('monthly');
      }
    } catch (error) {
      console.error('Error fetching Stripe config:', error);
    }
  };

  const handlePlanSelect = async (plan) => {
    if (plan.name === subscription?.currentPlan.name) {
      return; // Already on this plan
    }

    setSelectedPlan(plan);

    // If upgrading to a paid plan and Stripe is enabled, redirect to checkout
    if (plan.name !== 'free' && stripeConfig?.stripeEnabled && !subscription?.subscription.stripeSubscriptionId) {
      await handleStripeCheckout(plan);
    }
  };

  const handleStripeCheckout = async (plan) => {
    if (!stripeConfig?.stripeEnabled) {
      setToast({ message: 'Payment processing is not available', type: 'error' });
      return;
    }

    setProcessingPayment(true);
    setToast(null);

    try {
      // Create checkout session
      const res = await axios.post('/api/billing/create-checkout', {
        plan: plan.name,
        billingCycle: billingCycle
      });

      // Load Stripe and redirect to checkout
      const StripeLib = await loadStripe();
      const stripe = StripeLib(stripeConfig.publishableKey);

      // Redirect to Stripe Checkout
      const { error } = await stripe.redirectToCheckout({
        sessionId: res.data.sessionId
      });

      if (error) {
        console.error('Stripe redirect error:', error);
        setToast({ message: error.message, type: 'error' });
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setToast({
        message: error.response?.data?.error || 'Failed to start checkout',
        type: 'error'
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleManageBilling = async () => {
    if (!stripeConfig?.stripeEnabled) {
      setToast({ message: 'Billing management is not available', type: 'error' });
      return;
    }

    setProcessingPayment(true);
    try {
      const res = await axios.post('/api/billing/create-portal');
      window.location.href = res.data.url;
    } catch (error) {
      console.error('Portal error:', error);
      setToast({
        message: error.response?.data?.error || 'Failed to open billing portal',
        type: 'error'
      });
      setProcessingPayment(false);
    }
  };

  const handlePlanChange = async () => {
    if (!selectedPlan) return;

    // If it's a paid plan and Stripe is enabled, use Stripe checkout
    if (selectedPlan.name !== 'free' && stripeConfig?.stripeEnabled && !subscription?.subscription.stripeSubscriptionId) {
      await handleStripeCheckout(selectedPlan);
      return;
    }

    // Otherwise, use manual plan change
    setToast(null);
    setLoading(true);

    try {
      const res = await axios.post('/api/billing/change-plan', {
        newPlan: selectedPlan.name,
        billingCycle: billingCycle
      });

      setToast({ message: res.data.message, type: 'success' });
      setSelectedPlan(null);
      await fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to change plan';
      const isAlreadyOnPlan = errorMsg.toLowerCase().includes('already on this plan');
      setToast({
        message: errorMsg,
        type: isAlreadyOnPlan ? 'info' : 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setToast(null);
    setLoading(true);

    try {
      const res = await axios.post('/api/billing/cancel');
      setToast({ message: res.data.message, type: 'success' });
      setShowCancelConfirm(false);
      await fetchData();
    } catch (error) {
      setToast({
        message: error.response?.data?.error || 'Failed to cancel subscription',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getPlanBadgeColor = (planName) => {
    const colors = {
      free: '#6b7280',
      starter: '#3b82f6',
      growth: '#8b5cf6',
      enterprise: '#f59e0b'
    };
    return colors[planName] || '#6b7280';
  };

  // Format alert channels for display (only show email since slack/sms aren't implemented)
  const formatAlertChannels = (channels) => {
    if (!channels || channels.length === 0) return 'Email alerts';
    return channels.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(', ') + ' alerts';
  };

  if (loading && !subscription) {
    return (
      <div className="account-billing-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const hasActiveStripeSubscription = subscription?.subscription.stripeSubscriptionId && subscription?.subscription.status === 'active';
  // Annual toggle removed - only monthly billing for now

  return (
    <div className="account-billing-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="page-header">
        <h1>Account & Billing</h1>
        <p>Manage your subscription and payment settings</p>
      </div>

      {/* Current Plan Section */}
      <div className="billing-card current-plan-card">
        <h2>Current Plan</h2>
        <div className="current-plan-details">
          <div className="plan-badge-large" style={{ background: getPlanBadgeColor(subscription?.currentPlan.name) }}>
            {subscription?.currentPlan.displayName}
          </div>
          <div className="plan-stats">
            <div className="stat-item">
              <span className="stat-label">Monitoring Objects</span>
              <span className="stat-value">
                {subscription?.usage.used} / {subscription?.currentPlan.limits.monitoringObjects}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Business Units</span>
              <span className="stat-value">
                {subscription?.currentPlan.limits.businessUnits === 999 ? 'Unlimited' : subscription?.currentPlan.limits.businessUnits}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Check Frequency</span>
              <span className="stat-value">
                {subscription?.currentPlan.limits.minCheckIntervalMinutes === 1440
                  ? 'Daily'
                  : subscription?.currentPlan.limits.minCheckIntervalMinutes === 60
                    ? 'Hourly'
                    : `${subscription?.currentPlan.limits.minCheckIntervalMinutes} min`}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Data Retention</span>
              <span className="stat-value">{subscription?.currentPlan.limits.dataRetentionDays} days</span>
            </div>
          </div>
        </div>

        {subscription?.subscription.status !== 'none' && subscription?.subscription.periodEnd && (
          <div className="subscription-info">
            <p>
              <strong>Status:</strong>{' '}
              <span className={`status-badge status-${subscription.subscription.status}`}>
                {subscription.subscription.status}
              </span>
            </p>
            <p>
              <strong>Next billing date:</strong> {formatDate(subscription.subscription.periodEnd)}
            </p>
            {subscription?.paymentMethod?.last4 && (
              <p>
                <strong>Payment method:</strong> {subscription.paymentMethod.brand} ending in {subscription.paymentMethod.last4}
              </p>
            )}
          </div>
        )}

        <div className="plan-actions">
          {hasActiveStripeSubscription && (
            <button
              className="btn btn-secondary manage-btn"
              onClick={handleManageBilling}
              disabled={processingPayment}
            >
              {processingPayment ? 'Loading...' : 'Manage Billing'}
            </button>
          )}

          {subscription?.currentPlan.name !== 'free' && (
            <button
              className="btn btn-danger cancel-btn"
              onClick={() => setShowCancelConfirm(true)}
              disabled={loading || processingPayment}
            >
              Cancel Subscription
            </button>
          )}
        </div>
      </div>

      {/* Available Plans */}
      <div className="billing-card">
        <h2>Available Plans</h2>
        <div className="billing-toggle">
          <button
            className={`toggle-btn ${billingCycle === "monthly" ? "active" : ""}`}
            onClick={() => setBillingCycle("monthly")}
          >
            Monthly
          </button>
          <button
            className={`toggle-btn ${billingCycle === "annual" ? "active" : ""}`}
            onClick={() => setBillingCycle("annual")}
          >
            Annual (Save at least 20%)
          </button>
        </div>

        <div className="plans-grid">
          {availablePlans.map((plan) => {
            const isCurrentPlan = plan.name === subscription?.currentPlan.name;
            const price = billingCycle === 'monthly' ? plan.pricing.monthly : plan.pricing.annual;
            const monthlyPrice = billingCycle === 'annual' ? plan.pricing.annual / 12 : price;

            return (
              <div
                key={plan.name}
                className={`plan-card ${isCurrentPlan ? 'current-plan' : ''} ${selectedPlan?.name === plan.name ? 'selected' : ''}`}
                onClick={() => { if (isCurrentPlan) { setToast({ message: 'You are already on this plan', type: 'info' }); } else { handlePlanSelect(plan); } }}
              >
                {isCurrentPlan && <div className="current-badge">Current Plan</div>}

                <div className="plan-header">
                  <h3>{plan.displayName}</h3>
                  <div className="plan-price">
                    <span className="price-amount">{formatPrice(monthlyPrice)}</span>
                    <span className="price-period">/month</span>
                  </div>
                  <div className="billing-note">{billingCycle === "annual" ? (price > 0 ? `Billed ${formatPrice(price)} annually` : " ") : " "}</div>
                </div>

                <div className="plan-features">
                  <div className="feature">
                    <span className="feature-icon">&#10003;</span>
                    <span>{plan.monitoringObjects} monitoring objects</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">&#10003;</span>
                    <span>
                      {plan.businessUnits === 999 ? 'Unlimited' : plan.businessUnits} business unit{plan.businessUnits !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">&#10003;</span>
                    <span>
                      {plan.minCheckIntervalMinutes === 1440
                        ? 'Daily checks'
                        : plan.minCheckIntervalMinutes === 60
                          ? 'Hourly checks'
                          : `${plan.minCheckIntervalMinutes}-min checks`}
                    </span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">&#10003;</span>
                    <span>{plan.dataRetentionDays}-day data retention</span>
                  </div>
                  <div className="feature">
                    <span className="feature-icon">&#10003;</span>
                    <span>{formatAlertChannels(plan.alertChannels)}</span>
                  </div>
                </div>

                {!isCurrentPlan && (
                  <button
                    className={`btn ${selectedPlan?.name === plan.name ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanSelect(plan);
                    }}
                    disabled={processingPayment}
                  >
                    {processingPayment && selectedPlan?.name === plan.name ? 'Processing...' : selectedPlan?.name === plan.name ? 'Selected' : 'Select Plan'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {selectedPlan && selectedPlan.name !== subscription?.currentPlan.name && !processingPayment && (
          <div className="plan-change-actions">
            <button className="btn btn-primary btn-large" onClick={handlePlanChange} disabled={loading}>
              {loading ? 'Processing...' : `${selectedPlan.name === 'free' ? 'Downgrade to' : 'Upgrade to'} ${selectedPlan.displayName} Plan`}
            </button>
            <button className="btn btn-secondary" onClick={() => setSelectedPlan(null)}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Subscription History */}
      {history.length > 0 && (
        <div className="billing-card">
          <h2>Subscription History</h2>
          <div className="history-table">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Change Type</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.created_at)}</td>
                    <td>
                      <span className={`change-type-badge ${item.change_type}`}>
                        {item.change_type}
                      </span>
                    </td>
                    <td>{item.old_plan || '—'}</td>
                    <td>{item.new_plan}</td>
                    <td className="notes-cell">{item.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="modal-overlay" onClick={() => setShowCancelConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Cancel Subscription?</h3>
            <p>
              Are you sure you want to cancel your subscription?
              {hasActiveStripeSubscription
                ? ' Your subscription will remain active until the end of the current billing period, then you will be downgraded to the Free plan.'
                : ' Your account will be immediately downgraded to the Free plan.'}
            </p>
            <div className="modal-actions">
              <button className="btn btn-danger" onClick={handleCancelSubscription} disabled={loading}>
                {loading ? 'Cancelling...' : 'Yes, Cancel Subscription'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowCancelConfirm(false)}>
                Keep Subscription
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountBilling;
